import { ChangeDetectionStrategy, Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SettingsService } from '@api/settings.service';
import {
  SendStatisticsService,
  StatisticsActionKey,
  StatisticsContextEnum,
} from '@shared/api/send-statistics.service';
import { BaseObject } from '@shared/base/base-object';
import { OrderState } from '@shared/states/order.state';
import { ThemeState } from '@shared/states/theme.state';
import { UserState } from '@shared/states/user.state';
import { BreakpointTypeEnum } from '@shared/types/screen-breakpoints';
import { DialogService } from '@ui/dialog/dialog.service';
import { MainSidebarState } from '@ui/main-sidebar/main-sidebar.state';
import { BehaviorSubject, filter, first } from 'rxjs';
import { ChangeLocationResult } from './dialogs/change-location/change-location.types';
import { ChangeLocationFactory } from './dialogs/change-location/change-location.factory';
import { NavPanelState } from '@shared/states/nav-panel.state';

@Component({
  selector: 'app-main-header',
  templateUrl: './main-header.component.html',
  styleUrls: ['./main-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.mobile]': 'isMobileScreen',
    '[class.hidden]': 'isWizardPage$.value',
    '[class.wizard-flow]': 'isWizardPage$.value',
    '[class.app-border-bottom]': 'isAvailablePage$.value',
    '[class.app-border-addit]': 'isAvailablePage$.value',
  },
})
export class MainHeaderComponent extends BaseObject {
  public isAvailablePage$ = new BehaviorSubject<boolean>(true);
  public isWizardPage$ = new BehaviorSubject<boolean>(true);
  public isProfilePage$ = new BehaviorSubject<boolean>(true);

  public readonly BreakpointTypeEnum = BreakpointTypeEnum;

  public get isMobileScreen(): boolean {
    return [
      BreakpointTypeEnum.XXSmall,
      BreakpointTypeEnum.XSmall,
      BreakpointTypeEnum.Small,
      BreakpointTypeEnum.HalfSmall,
    ].includes(this.settingsService.screenBreakpoint$.value);
  }

  constructor(
    public themeState: ThemeState,
    public userState: UserState,
    public orderState: OrderState,
    public mainSidebarState: MainSidebarState,
    public navPanelState: NavPanelState,
    public settingsService: SettingsService,
    private statistics: SendStatisticsService,
    private dialogService: DialogService,
    private router: Router,
  ) {
    super();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;

        this.isAvailablePage$.next(url !== '/login' && url !== '/sketch');
        this.isWizardPage$.next(url === '/wizard/order-create-flow');
        this.isProfilePage$.next(url === '/account/profile');
      });
  }

  public openMobileSidebar(): void {
    this.mainSidebarState.openMobile();
  }

  public changeLocation(): void {
    const mobile = this.settingsService.screenBreakpoint$.value === 'XXSmall';

    this.statistics
      .sendStat(StatisticsActionKey.CHANGE_LOCATION_BUTTON, StatisticsContextEnum.STEP_WIZARD_1)
      .pipe(first())
      .subscribe();

    this.dialogService
      .openLazy<void, ChangeLocationResult>({ factory: ChangeLocationFactory }, null, {
        width: mobile ? 'calc(100% - 32px)' : '530px',
        mobile: mobile,
      })
      .closed$.pipe(first())
      .subscribe((result) => {
        if (!result) {
          this.statistics
            .sendStat(
              StatisticsActionKey.CHANGE_LOCATION_CLOSE_BUTTON,
              StatisticsContextEnum.CHANGE_LOCATION,
            )
            .pipe(first())
            .subscribe();

          return;
        }

        if (result.city) {
          this.orderState.currentCity$.next(result.city);
        } else if (result.manualSelect) {
          this.navPanelState.manualSelect$.next(true);
          this.navPanelState.manualSelect$.next(false);
        }
      });
  }
}
